import { addressSchema } from "@/features/settings/basic-settings/validations/company";
import { LegalForm } from "@/queries/companies/fetch";
import { t } from "i18next";
import * as z from "zod";

export const formSchema = z
  .object({
    companyName: z.string().min(1, { message: "Firmenname ist erforderlich" }),
    companyDescription: z
      .string()
      .min(1, { message: "Geschäftsmodell ist erforderlich" }),
    addresses: z
      .array(addressSchema)
      .min(1, { message: "Mindestens eine Adresse ist erforderlich" }),
    vatId: z.string().optional(),
    accountLength: z.enum(["4", "5", "6"], {
      required_error: "Kontenlänge ist erforderlich",
    }),
    skr: z.enum(["03", "04"], { required_error: "SKR ist erforderlich" }),
    vorsteuerabzug: z.boolean().default(false),
    selectedUsers: z.array(z.string()),
    hasCostCenters: z.boolean().default(false),
    costCentersFile: z.any().optional(),
    skipDebtorsCreditorsImport: z.boolean().default(false),
    debtorsCreditorsFile: z.custom<File | null>().nullable().default(null),
    debtorsCreditors: z
      .object({
        debtors: z.array(
          z.object({
            accountNumber: z.number(),
            name: z.string(),
            vatId: z.string().optional(),
            customerNumber: z.string().optional(),
          }),
        ),
        creditors: z.array(
          z.object({
            accountNumber: z.number(),
            name: z.string(),
            vatId: z.string().optional(),
            customerNumber: z.string().optional(),
          }),
        ),
      })
      .optional(),
    costCenters: z
      .array(
        z.object({
          identifier: z.string(),
          name: z.string(),
          description: z.string().optional(),
        }),
      )
      .optional(),
    skipAccountsImport: z.boolean().default(false),
    accountsFile: z.custom<File | null>().nullable().default(null),
    accounts: z
      .array(
        z.object({
          accountNo: z.string(),
          accountName: z.string(),
          accountDescription: z.string().optional(),
          accountType: z.string().optional(),
          parentAccountNo: z.string().optional(),
        }),
      )
      .optional(),
    companyType: z
      .enum(["STANDARD", "CONSTRUCTION", "CLEANING"])
      .default("STANDARD"),
    legalForm: z.nativeEnum(LegalForm, {
      required_error: t(
        "mandanten_create.step1.validation.legal_form_required",
      ),
    }),
  })
  .refine(
    (data) => {
      // Validate Debtors/Creditors
      if (!data.skipDebtorsCreditorsImport && !data.debtorsCreditorsFile) {
        return false;
      }
      // Validate Accounts
      if (!data.skipAccountsImport && !data.accountsFile) {
        return false;
      }
      return true;
    },
    {
      message:
        "Bitte laden Sie entweder eine Datei hoch oder wählen Sie 'Kein Import erforderlich'",
      path: ["skipDebtorsCreditorsImport", "skipAccountsImport"],
    },
  )
  .refine(
    (data) => {
      if (data.hasCostCenters) {
        return !!data.costCentersFile;
      }
      return true;
    },
    {
      message:
        "Bitte geben Sie die Kostenstellen in Text- oder Dateiformat an oder geben Sie an, dass keine Kostenstellen importiert werden sollen",
      path: ["hasCostCenters"],
    },
  )
  .refine(
    (data) => {
      if (data.hasCostCenters && data.costCentersFile) {
        return !!data.costCenters;
      }
      return true;
    },
    {
      message:
        "Bitte warten Sie, bis die Kostenstellen-Datei verarbeitet wurde",
      path: ["costCentersFile"],
    },
  );

export type FormValues = z.infer<typeof formSchema>;
